// Compact inline meter row rendered alongside the asset's regular attributes.
// One row per attribute on the asset whose schema flag `isMeter === true`.
// Collapsed: name, current value, micro-sparkline, IconButton actions.
// Expanded: adds full-size chart, source legend, threshold reference lines.

import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Icon, IconButton, Modal, Snackbar, TextField, Tooltip as MuiTooltip } from '@atom/mui';
import colors from '@atom/styles/colors';
import type { MeterReading } from '@atom/types/meter';

import classes from './meterAttributeCard.module.css';

type MeterAttributeCardProps = {
  assetId: string;
  attributeId: string;
  attributeName: string;
  unit?: string;
};

type ApiResponse = {
  config?: { ratePerDay: number; sensorFeedEnabled: boolean; anchorReading: { value: number; recordedAt: string } };
  readings: MeterReading[];
  currentValue: number | null;
};

// Source palette picked for max separability — each source is a clearly
// distinct hue from the standard Atom brand palette. Avoid teal/violet pair
// (too close to blue) in favor of green/orange so all four dots read at a
// glance against a white background.
const SOURCE_COLORS: Record<MeterReading['source'], string> = {
  SENSOR_FEED: colors.brand.blue,
  WORK_ORDER: colors.brand.green,
  MANUAL: colors.brand.orange,
  AUTO_TICK: colors.neutral.gray,
};

const SOURCE_LABEL: Record<MeterReading['source'], string> = {
  AUTO_TICK: 'Auto-tick',
  SENSOR_FEED: 'Sensor',
  WORK_ORDER: 'Work Order',
  MANUAL: 'Manual',
};

// Use the server-computed currentValue (based on mockNow()) as the authoritative
// base, then apply real-time drift from the fetch timestamp. This keeps the
// display accurate when the demo mock clock is advanced ahead of wall time.
const computeDisplayedValue = (
  api: ApiResponse | null,
  fetchedAt: number,
): number | null => {
  if (!api || api.currentValue == null) return null;
  const rate = api.config?.ratePerDay ?? 0;
  const elapsedDays = (Date.now() - fetchedAt) / 86400000;
  return Math.max(0, api.currentValue + rate * elapsedDays);
};

const MeterAttributeCard = (props: MeterAttributeCardProps) => {
  const { assetId, attributeId, attributeName, unit } = props;

  const [api, setApi] = useState<ApiResponse | null>(null);
  const [fetchedAt, setFetchedAt] = useState<number>(() => Date.now());
  const [openModal, setOpenModal] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [tick, setTick] = useState(0);
  const [thresholds, setThresholds] = useState<number[]>([]);
  const [expanded, setExpanded] = useState(false);

  const fetchMeter = () => {
    fetch(`/api/v1/assets/${assetId}/meters/${attributeId}/readings`)
      .then(r => r.json())
      .then((d: ApiResponse) => {
        setApi(d);
        setFetchedAt(Date.now());
      })
      .catch(() => {});
  };

  useEffect(() => {
    fetchMeter();
  }, [assetId, attributeId]);

  // Re-fetch when demo controls advance the mock clock or push a sensor reading,
  // so the displayed value reflects the new server-side currentValue immediately.
  useEffect(() => {
    const handler = () => fetchMeter();
    window.addEventListener('meters:refresh', handler);
    return () => window.removeEventListener('meters:refresh', handler);
  }, [assetId, attributeId]);

  useEffect(() => {
    const i = window.setInterval(() => setTick(t => t + 1), 10_000);
    return () => window.clearInterval(i);
  }, []);

  useEffect(() => {
    fetch('/api/v1/workTemplates?limit=200')
      .then(r => r.json())
      .then((arr: any[]) => {
        // Scan every schedule on every template; a single template can carry
        // both calendar and numeric schedules so we can't read `t.schedule`
        // singular.
        const found: number[] = [];
        (arr ?? []).forEach((t: any) => {
          const schedules: any[] =
            t.schedules && t.schedules.length > 0
              ? t.schedules
              : t.schedule
                ? [t.schedule]
                : [];
          schedules.forEach(s => {
            if (
              s?.active &&
              s?.trigger?.kind === 'NUMERIC_FIELD' &&
              s.trigger.attributeId === attributeId &&
              // SINGLE-threshold triggers have a fixed `threshold`. INTERVAL
              // triggers recur on a moving boundary (lastServiced + amount) so
              // there's no single dashed line to draw — skip them here.
              s.trigger.mode !== 'INTERVAL' &&
              Number.isFinite(s.trigger.threshold)
            ) {
              found.push(s.trigger.threshold);
            }
          });
        });
        setThresholds(Array.from(new Set(found)));
      });
  }, [attributeId]);

  const displayed = useMemo(
    () => computeDisplayedValue(api, fetchedAt),
    [api, fetchedAt, tick],
  );

  const chartData = useMemo(() => {
    if (!api) return [];
    return api.readings.map(r => ({
      x: new Date(r.recordedAt).getTime(),
      y: r.value,
      source: r.source,
    }));
  }, [api]);

  // Y-axis domain + ticks for the expanded chart. We compute these here so
  // threshold values get rendered as explicit Y-axis tick labels alongside
  // the auto-spaced bounds (no in-chart label needed; the dashed line + the
  // tick value tell the story together).
  const yAxisConfig = useMemo(() => {
    if (chartData.length === 0) {
      return { domain: [0, 1] as [number, number], ticks: [0, 1] };
    }
    const dataMin = Math.min(...chartData.map(d => d.y));
    const dataMax = Math.max(...chartData.map(d => d.y));
    const lo = thresholds.length ? Math.min(dataMin, ...thresholds) : dataMin;
    const hi = thresholds.length ? Math.max(dataMax, ...thresholds) : dataMax;
    const range = Math.max(1, hi - lo);
    const yMin = Math.floor(lo - range * 0.1);
    const yMax = Math.ceil(hi + range * 0.1);
    // Bounds + midpoint as the auto ticks. Thresholds get appended so they
    // render as labeled Y-axis ticks (highlighted red by the custom tick
    // renderer). Drop any auto tick within 8% of a threshold so labels
    // don't overlap.
    const minGap = range * 0.08;
    const auto = [yMin, Math.round((yMin + yMax) / 2), yMax].filter(
      t => !thresholds.some(th => Math.abs(th - t) < minGap),
    );
    const ticks = Array.from(new Set([...auto, ...thresholds])).sort(
      (a, b) => a - b,
    );
    return { domain: [yMin, yMax] as [number, number], ticks };
  }, [chartData, thresholds]);

  const lastReading = api?.readings.length
    ? api.readings.reduce((a, b) =>
        new Date(a.recordedAt).getTime() >= new Date(b.recordedAt).getTime()
          ? a
          : b,
      )
    : null;

  const lastReadingTooltip = lastReading
    ? `Last reading: ${SOURCE_LABEL[lastReading.source]} · ${new Date(lastReading.recordedAt).toLocaleString()}`
    : 'No readings recorded yet';

  const handleSubmit = async () => {
    const value = Number(inputValue);
    if (!Number.isFinite(value)) {
      Snackbar.error({ message: 'Enter a numeric value' });
      return;
    }
    const res = await fetch(
      `/api/v1/assets/${assetId}/meters/${attributeId}/readings`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value, source: 'MANUAL' }),
      },
    );
    if (!res.ok) {
      Snackbar.error({ message: 'Failed to record reading' });
      return;
    }
    const body = await res.json();
    setOpenModal(false);
    setInputValue('');
    fetchMeter();
    if (body.generatedWorkOrders?.length > 0) {
      const wo = body.generatedWorkOrders[0];
      Snackbar.info({
        message: `Work order auto-created from threshold: ${wo.name}`,
      });
    } else {
      Snackbar.info({ message: 'Reading recorded' });
    }
    window.dispatchEvent(new CustomEvent('notifications:refresh'));
  };

  const unitSuffix = unit ? ` (${unit})` : '';

  return (
    <div className={classes.row}>
      <div className={classes.rowMain}>
        <div className={classes.label}>
          {attributeName}
          {unitSuffix}
        </div>
        <div className={classes.valueGroup}>
          <MuiTooltip title={lastReadingTooltip}>
            <span className={classes.value}>
              {displayed != null ? displayed.toFixed(1) : '—'}
              {unit ? <span className={classes.unit}>{unit}</span> : null}
            </span>
          </MuiTooltip>
          <div className={classes.sparkline}>
            {/* Fixed-size LineChart (not ResponsiveContainer) — the tiny
                inline chart rendered blank when its container measured 0 on
                mount. A category x-axis keeps the curve simple. */}
            <LineChart
              width={184}
              height={28}
              data={chartData}
              margin={{ top: 4, right: 4, bottom: 4, left: 4 }}
            >
              <YAxis
                type="number"
                domain={[
                  (dataMin: number) =>
                    Math.min(dataMin, thresholds.length ? Math.min(...thresholds) : dataMin),
                  (dataMax: number) =>
                    Math.max(dataMax, thresholds.length ? Math.max(...thresholds) : dataMax),
                ]}
                hide
              />
              <Tooltip
                cursor={{ stroke: colors.neutral.gray, strokeWidth: 1 }}
                contentStyle={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                labelFormatter={() => ''}
                formatter={(v: any, _name: string, p: any) => [
                  `${(v as number).toFixed(1)} ${unit ?? ''}`.trim(),
                  SOURCE_LABEL[p?.payload?.source as MeterReading['source']] ??
                    'Reading',
                ]}
              />
              <Line
                type="monotone"
                dataKey="y"
                stroke={colors.brand.blue}
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
              />
              {thresholds.map(th => (
                <ReferenceLine
                  key={th}
                  y={th}
                  stroke={colors.utility.error}
                  strokeDasharray="2 2"
                  strokeWidth={1}
                />
              ))}
            </LineChart>
          </div>
          <div className={clsx(classes.actions, expanded && classes.expandedShown)}>
            <MuiTooltip title="Record meter reading">
              <IconButton size="small" onClick={() => setOpenModal(true)}>
                <Icon className={classes.actionIcon}>add</Icon>
              </IconButton>
            </MuiTooltip>
            <MuiTooltip title={expanded ? 'Hide history' : 'Show history'}>
              <IconButton size="small" onClick={() => setExpanded(e => !e)}>
                <Icon className={classes.actionIcon}>
                  {expanded ? 'expand_less' : 'expand_more'}
                </Icon>
              </IconButton>
            </MuiTooltip>
          </div>
        </div>
      </div>

      {expanded && (
        <div className={classes.expanded}>
          <div className={classes.chartWrap}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={colors.neutral.typhoon}
                />
                <XAxis
                  dataKey="x"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={(t) => new Date(t).toLocaleDateString()}
                  stroke={colors.neutral.gray}
                  fontSize={11}
                />
                <YAxis
                  type="number"
                  domain={yAxisConfig.domain}
                  ticks={yAxisConfig.ticks}
                  interval={0}
                  tick={(props: any) => {
                    const { x, y, payload } = props;
                    const isThreshold = thresholds.includes(payload.value);
                    return (
                      <text
                        x={x}
                        y={y}
                        dy={4}
                        textAnchor="end"
                        fontSize={11}
                        fill={
                          isThreshold
                            ? colors.utility.error
                            : colors.neutral.gray
                        }
                        fontWeight={isThreshold ? 600 : 400}
                      >
                        {payload.value}
                      </text>
                    );
                  }}
                  allowDecimals={false}
                  stroke={colors.neutral.gray}
                  width={56}
                />
                <Tooltip
                  labelFormatter={(t) => new Date(t).toLocaleString()}
                  formatter={(v: any, _name: string, p: any) => [
                    `${v} ${unit ?? ''}`.trim(),
                    SOURCE_LABEL[p?.payload?.source as MeterReading['source']] ??
                      'Reading',
                  ]}
                />
                {thresholds.map(th => (
                  <ReferenceLine
                    key={th}
                    y={th}
                    stroke={colors.utility.error}
                    strokeDasharray="4 4"
                  />
                ))}
                <Line
                  type="monotone"
                  dataKey="y"
                  stroke={colors.neutral.silver}
                  strokeWidth={1.5}
                  dot={(dotProps: any) => {
                    const { cx, cy, payload, index } = dotProps;
                    if (typeof cx !== 'number' || typeof cy !== 'number')
                      return <></>;
                    const fill =
                      SOURCE_COLORS[
                        payload?.source as MeterReading['source']
                      ] ?? colors.brand.blue;
                    return (
                      <circle
                        key={`dot-${index}`}
                        cx={cx}
                        cy={cy}
                        r={5}
                        fill={fill}
                        stroke={colors.neutral.white}
                        strokeWidth={1.5}
                      />
                    );
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className={classes.legend}>
            {(
              ['SENSOR_FEED', 'WORK_ORDER', 'MANUAL', 'AUTO_TICK'] as MeterReading['source'][]
            ).map(s => (
              <span key={s} className={classes.legendItem}>
                <span
                  className={classes.swatch}
                  style={{ backgroundColor: SOURCE_COLORS[s] }}
                />
                {SOURCE_LABEL[s]}
              </span>
            ))}
            {thresholds.length > 0 && (
              <span className={classes.legendItem}>
                <span
                  className={classes.thresholdSwatch}
                  style={{ borderTopColor: colors.utility.error }}
                />
                PM trigger threshold
              </span>
            )}
          </div>
        </div>
      )}

      <Modal
        title={`Update ${attributeName}`}
        open={openModal}
        onConfirm={handleSubmit}
        onCancel={() => setOpenModal(false)}
        confirmButtonText="Record reading"
      >
        <div className={classes.modalField}>
          <TextField
            label={`Value${unit ? ` (${unit})` : ''}`}
            type="number"
            autoFocus
            value={inputValue}
            onChange={(e: any) => setInputValue(e.target.value)}
            placeholder={displayed != null ? displayed.toFixed(1) : ''}
          />
          <div className={classes.modalHint}>
            Source: Manual override · Threshold lines on the chart will
            auto-fire matching PM templates if crossed.
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default MeterAttributeCard;
